import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Checkbox, FormControlLabel,
    FormControl, InputLabel, Select, MenuItem, Button
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';

import { useAuth } from '../../contexts/AuthContext';

const OrderSidebar = ({
    order, items,
    orderType,
    onValidate,
    deliveryDate, setDeliveryDate,
    specialProduction, setSpecialProduction
}) => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState('');
    const [password, setPassword] = useState('');

    useEffect(() => {
        const fetchUsers = async () => {
            if (user?.is_wholesale_client) return;
            try {
                const response = await axios.get('/api/users/?is_active=true');
                setUsers(response.data.results || response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchUsers();
    }, [user?.is_wholesale_client]);

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);
    };

    const calculateTotalWeight = () => {
        return items.reduce((sum, item) => {
            const quantity = parseFloat(item.quantity || 0);
            const product = item.product || {};

            if (product.unit === 'kg') {
                return sum + quantity;
            }
            const weightPerUnit = parseFloat(product.weight_per_unit || 0);
            return sum + (quantity * weightPerUnit);
        }, 0);
    };

    const handleValidate = async () => {
        try {
            if (user?.is_wholesale_client || orderType === 'business') {
                await onValidate(null, null);
            } else {
                await onValidate(selectedSeller, password);
            }
        } catch (err) {
            // El error ya es manejado y expuesto en el estado del hook useOrder
            console.error('Validation failed:', err);
        }
    };

    // Date logic helpers could be moved to utils
    const getMinDate = () => {
        if (!specialProduction) return undefined;
        const today = new Date();
        const minDate = new Date(today);
        minDate.setDate(today.getDate() + 7);
        return minDate.toISOString().split('T')[0];
    };

    return (
        <Box sx={{ p: 2, bgcolor: '#f6f7f8', height: '100%', borderLeft: '1px solid #e7edf3', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Totals */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">Cantidad Total (kg)</Typography>
                    <Typography fontWeight="500">{calculateTotalWeight().toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">Items</Typography>
                    <Typography fontWeight="500">{items.length}</Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid #e7edf3', mt: 1, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight="700">Total</Typography>
                    <Typography variant="h5" fontWeight="700" color="primary">
                        ${calculateTotal().toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </Typography>
                </Box>
            </Box>

            {/* Delivery Info */}
            <Box sx={{ borderTop: '1px solid #e7edf3', pt: 1 }}>
                <FormControlLabel
                    sx={{ my: -0.5 }}
                    control={
                        <Checkbox
                            size="small"
                            checked={specialProduction}
                            onChange={(e) => {
                                setSpecialProduction(e.target.checked);
                                if (!e.target.checked && deliveryDate) setDeliveryDate(null);
                            }}
                        />
                    }
                    label={<Typography variant="body2">Producción Especial (7 días anticipación)</Typography>}
                />
                {specialProduction && (
                    <TextField
                        fullWidth
                        type="date"
                        label="Fecha de Entrega"
                        value={deliveryDate || ''}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: getMinDate() }}
                        size="small"
                        sx={{ mt: 1 }}
                    />
                )}
            </Box>

            {/* Validation */}
            <Box sx={{ borderTop: '1px solid #e7edf3', pt: 1.5 }}>
                {!user?.is_wholesale_client && orderType !== 'business' && (
                    <>
                        <Typography variant="subtitle2" fontWeight="600" mb={0.5}>Validar Vendedor</Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                            <InputLabel>Usuario</InputLabel>
                            <Select
                                value={selectedSeller}
                                onChange={(e) => setSelectedSeller(e.target.value)}
                                label="Usuario"
                            >
                                <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                                {users.map(u => (
                                    <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            type="password"
                            label="Contraseña"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            size="small"
                            sx={{ mb: 1.5 }}
                        />
                    </>
                )}
                
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleValidate}
                    disabled={items.length === 0 || (!user?.is_wholesale_client && orderType !== 'business' && (!selectedSeller || !password))}
                    sx={{ py: 1.5, fontWeight: 'bold' }}
                >
                    {user?.is_wholesale_client || orderType === 'business' ? 'Confirmar y Revisar Pedido' : 'Cargar Pedido'}
                </Button>
            </Box>
        </Box>
    );
};

export default OrderSidebar;
